<template>
    <div class="app-container">
      <!-- 顶部搜索和筛选区域 -->
      <el-card class="filter-card">
        <el-form :inline="true" :model="queryParams" class="search-form">
          <el-form-item label="报告类型">
            <el-select v-model="queryParams.reportType" placeholder="选择报告类型" clearable style="width: 180px">
              <el-option v-for="item in reportTypeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>
  
          <el-form-item label="患者姓名">
            <el-input v-model="queryParams.patientName" placeholder="输入患者姓名" clearable style="width: 180px"></el-input>
          </el-form-item>
  
          <el-form-item label="解读时间">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              style="width: 260px">
            </el-date-picker>
          </el-form-item>
  
          <el-form-item label="风险等级">
            <el-select v-model="queryParams.riskLevel" placeholder="选择风险等级" clearable style="width: 120px">
              <el-option v-for="item in riskOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>
  
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleQuery">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="resetQuery">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>
  
      <!-- 统计卡片 -->
      <el-row :gutter="20" class="stat-row">
        <el-col :span="6" v-for="(item, index) in statData" :key="index">
          <el-card class="stat-card" shadow="hover" :body-style="{ padding: '15px' }">
            <div class="stat-content">
              <div class="stat-icon" :style="{ backgroundColor: item.color }">
                <i :class="item.icon"></i>
              </div>
              <div class="stat-info">
                <div class="stat-title">{{ item.title }}</div>
                <div class="stat-value">{{ item.value }}</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
  
      <!-- 历史报告列表 -->
      <el-card class="table-card">
        <div slot="header" class="clearfix">
          <span><i class="el-icon-document-copy"></i> 解读记录列表</span>
          <div class="operation-buttons">
            <el-button type="success" icon="el-icon-upload2" size="small" @click="batchExport" :disabled="selection.length === 0">批量导出</el-button>
            <el-button type="danger" icon="el-icon-delete" size="small" @click="batchDelete" :disabled="selection.length === 0">批量删除</el-button>
          </div>
        </div>
  
        <el-table 
          v-loading="loading" 
          :data="historyList" 
          @selection-change="handleSelectionChange" 
          border
          stripe
          style="width: 100%">
          <el-table-column type="selection" width="55"></el-table-column>
          
          <el-table-column prop="reportId" label="报告ID" width="80"></el-table-column>
          
          <el-table-column prop="patientName" label="患者姓名" width="100">
            <template slot-scope="scope">
              <el-tooltip :content="scope.row.patientInfo" placement="top" effect="light">
                <span>{{ scope.row.patientName }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          
          <el-table-column prop="reportType" label="报告类型" width="120">
            <template slot-scope="scope">
              <el-tag :type="getReportTypeTag(scope.row.reportType)">{{ scope.row.reportType }}</el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="disease" label="疾病类型" width="100"></el-table-column>
          
          <el-table-column prop="analyzeTime" label="解读时间" width="170"></el-table-column>
          
          <el-table-column prop="riskLevel" label="风险等级" width="100">
            <template slot-scope="scope">
              <el-tag :type="getRiskTag(scope.row.riskLevel)" size="medium">{{ getRiskText(scope.row.riskLevel) }}</el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="summary" label="解读摘要" show-overflow-tooltip min-width="200"></el-table-column>
          
          <el-table-column label="操作" width="260" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" icon="el-icon-view" @click="viewDetail(scope.row)">查看</el-button>
              <el-button size="mini" type="success" icon="el-icon-download" @click="exportReport(scope.row)">导出</el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" @click="deleteReport(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
  
        <div class="pagination-container">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="queryParams.pageNum"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="queryParams.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total">
          </el-pagination>
        </div>
      </el-card>
  
      <!-- 详细查看弹窗 -->
      <el-dialog 
        :title="'AI报告解读详情 - ' + (currentReport ? currentReport.reportType : '')" 
        :visible.sync="dialogVisible" 
        width="80%" 
        :before-close="handleClose"
        append-to-body>
        <div v-if="currentReport" class="report-detail">
          <div class="patient-info">
            <h3>患者信息</h3>
            <el-descriptions :column="3" border size="medium">
              <el-descriptions-item label="患者姓名">{{ currentReport.patientName }}</el-descriptions-item>
              <el-descriptions-item label="年龄">{{ currentReport.patientAge }}岁</el-descriptions-item>
              <el-descriptions-item label="性别">{{ currentReport.patientGender }}</el-descriptions-item>
              <el-descriptions-item label="病历号">{{ currentReport.medicalRecordNo }}</el-descriptions-item>
              <el-descriptions-item label="联系电话">{{ currentReport.contactPhone }}</el-descriptions-item>
              <el-descriptions-item label="解读时间">{{ currentReport.analyzeTime }}</el-descriptions-item>
            </el-descriptions>
          </div>
  
          <el-divider></el-divider>
  
          <div class="analysis-result">
            <div class="risk-panel">
              <div class="risk-level" :class="currentReport.riskLevel">
                <i class="el-icon-warning"></i> {{ getRiskText(currentReport.riskLevel) }}
              </div>
              <p class="analysis-summary">{{ currentReport.summary }}</p>
            </div>
  
            <el-tabs v-model="detailActiveTab">
              <el-tab-pane label="解读详情" name="detail">
                <el-collapse accordion v-model="detailActiveCollapse">
                  <el-collapse-item title="关键发现解读" name="1">
                    <div v-html="currentReport.keyFindings"></div>
                  </el-collapse-item>
                  <el-collapse-item title="诊断建议" name="2">
                    <div v-html="currentReport.suggestions"></div>
                  </el-collapse-item>
                  <el-collapse-item title="健康科普" name="3">
                    <div v-html="currentReport.education"></div>
                  </el-collapse-item>
                </el-collapse>
              </el-tab-pane>
              <el-tab-pane label="原始报告" name="origin">
                <div class="origin-report">
                  <div v-html="currentReport.originalReport"></div>
                </div>
              </el-tab-pane>
              <el-tab-pane label="解读历史" name="history" v-if="currentReport.previousAnalysis && currentReport.previousAnalysis.length > 0">
                <el-timeline>
                  <el-timeline-item
                    v-for="(item, index) in currentReport.previousAnalysis"
                    :key="index"
                    :timestamp="item.time"
                    :color="getTimelineColor(item.riskLevel)">
                    <div class="timeline-content">
                      <h4>{{ item.time }} - {{ getRiskText(item.riskLevel) }}</h4>
                      <p>{{ item.summary }}</p>
                      <el-button type="text" @click="compareTo(item)">与当前对比</el-button>
                    </div>
                  </el-timeline-item>
                </el-timeline>
              </el-tab-pane>
            </el-tabs>
            
            <div class="action-buttons">
              <el-button type="primary" icon="el-icon-printer" @click="printReport">打印报告</el-button>
              <el-button type="success" icon="el-icon-share" @click="shareToDoctors">分享给医生</el-button>
              <el-button type="info" icon="el-icon-download" @click="exportReport(currentReport)">导出PDF</el-button>
            </div>
          </div>
        </div>
      </el-dialog>
  
      <!-- 对比弹窗 -->
      <el-dialog
        title="解读结果对比"
        :visible.sync="compareDialogVisible"
        width="90%"
        append-to-body>
        <div v-if="compareData" class="compare-container">
          <el-row :gutter="20">
            <el-col :span="12">
              <div class="compare-panel">
                <div class="compare-header">
                  <h4>历史解读 ({{ compareData.previous.time }})</h4>
                  <div class="risk-level" :class="compareData.previous.riskLevel">
                    <i class="el-icon-warning"></i> {{ getRiskText(compareData.previous.riskLevel) }}
                  </div>
                </div>
                <div class="compare-content">
                  <p><b>摘要：</b>{{ compareData.previous.summary }}</p>
                  <div class="compare-findings" v-html="compareData.previous.keyFindings"></div>
                </div>
              </div>
            </el-col>
            <el-col :span="12">
              <div class="compare-panel">
                <div class="compare-header">
                  <h4>当前解读 ({{ currentReport.analyzeTime }})</h4>
                  <div class="risk-level" :class="currentReport.riskLevel">
                    <i class="el-icon-warning"></i> {{ getRiskText(currentReport.riskLevel) }}
                  </div>
                </div>
                <div class="compare-content">
                  <p><b>摘要：</b>{{ currentReport.summary }}</p>
                  <div class="compare-findings" v-html="currentReport.keyFindings"></div>
                </div>
              </div>
            </el-col>
          </el-row>
          <el-divider>
            <i class="el-icon-info"></i> 变化分析
          </el-divider>
          <div class="change-analysis">
            <p v-if="compareData.hasImproved" class="improved">
              <i class="el-icon-success"></i> 病情相比上次检查有所改善，风险等级从 {{ getRiskText(compareData.previous.riskLevel) }} 降低为 {{ getRiskText(currentReport.riskLevel) }}
            </p>
            <p v-else-if="compareData.hasWorsened" class="worsened">
              <i class="el-icon-warning"></i> 病情相比上次检查有所恶化，风险等级从 {{ getRiskText(compareData.previous.riskLevel) }} 提高为 {{ getRiskText(currentReport.riskLevel) }}
            </p>
            <p v-else class="stable">
              <i class="el-icon-info"></i> 病情相比上次检查基本稳定，风险等级保持在 {{ getRiskText(currentReport.riskLevel) }}
            </p>
            <div v-html="compareData.changeAnalysis"></div>
          </div>
        </div>
      </el-dialog>
    </div>
  </template>
  
  <script>
  export default {
    name: "AIReportHistory",
    data() {
      return {
        // 查询参数
        queryParams: {
          pageNum: 1,
          pageSize: 10,
          reportType: "",
          patientName: "",
          riskLevel: "",
          beginTime: "",
          endTime: ""
        },
        dateRange: [],
        loading: false,
        total: 0,
        selection: [],
        historyList: [],
        dialogVisible: false,
        compareDialogVisible: false,
        currentReport: null,
        compareData: null,
        detailActiveTab: "detail",
        detailActiveCollapse: "1",
        
        // 报告类型选项
        reportTypeOptions: [
          { value: "CT影像", label: "CT影像" },
          { value: "核磁共振", label: "核磁共振" },
          { value: "X光片", label: "X光片" },
          { value: "血液检验", label: "血液检验" },
          { value: "病理报告", label: "病理报告" },
          { value: "心电图", label: "心电图" },
          { value: "超声检查", label: "超声检查" }
        ],
        
        // 风险等级选项
        riskOptions: [
          { value: "high", label: "高度关注" },
          { value: "medium", label: "中度关注" },
          { value: "low", label: "一般关注" }
        ],
        
        // 统计数据
        statData: [
          { title: "本月解读总量", value: "124份", icon: "el-icon-document", color: "#409EFF" },
          { title: "高风险报告", value: "18份", icon: "el-icon-warning", color: "#F56C6C" },
          { title: "已分享报告", value: "36份", icon: "el-icon-share", color: "#67C23A" },
          { title: "平均解读时间", value: "28秒", icon: "el-icon-time", color: "#E6A23C" }
        ],
        
        // 模拟报告列表数据
        mockData: [
          {
            reportId: "AI2024050001",
            patientName: "许昊然",
            patientInfo: "许昊然，65岁，男，冠心病",
            patientAge: 65,
            patientGender: "男",
            medicalRecordNo: "P202405001",
            contactPhone: "138****1234",
            reportType: "CT影像",
            disease: "肺结节",
            analyzeTime: "2024-05-10 14:30:22",
            riskLevel: "high",
            summary: "检测到右肺上叶存在高度疑似恶性肿瘤的结节（恶性概率约85%）",
            keyFindings: "<ul><li><b>右肺上叶结节</b>：15mm×12mm的不规则结节，伴有毛刺征和胸膜凹陷征，这些是肺癌的典型影像学特征。</li><li><b>风险评估</b>：根据结节大小、形态和特征，属于高度疑似恶性病变，建议立即进行进一步检查。</li><li><b>双肺小结节</b>：1-3mm小结节多为良性病变，如炎症后改变、肉芽肿等，需定期随访。</li></ul>",
            suggestions: "<ul><li>建议尽快进行PET-CT检查评估全身情况</li><li>考虑CT引导下肺穿刺活检或纤维支气管镜检查获取病理</li><li>建议呼吸科和胸外科多学科会诊</li><li>3-6个月后复查胸部CT评估小结节变化</li></ul>",
            education: "<p>肺结节是胸部CT检查中常见的发现，根据大小、形态和特征可评估良恶性可能。</p><p>结节大于10mm、边缘不规则、有毛刺、胸膜凹陷等特征提示恶性可能性增加。</p><p>早期肺癌如能及时诊断治疗，5年生存率可达70%以上。</p>",
            originalReport: "<p><b>检查名称：</b> 胸部CT平扫</p><p><b>检查时间：</b> 2024-05-10</p><p><b>检查所见：</b></p><p>右肺上叶见一不规则结节影，大小约15mm×12mm，密度不均，边界模糊，毛刺征阳性，可见胸膜凹陷征。双肺散在小结节，直径1-3mm，边界清晰。</p><p>纵隔内未见明显肿大淋巴结。心影大小正常。</p><p><b>检查提示：</b></p><p>1. 右肺上叶结节影，考虑恶性可能，建议进一步检查。</p><p>2. 双肺散在小结节，考虑良性可能，建议随访观察。</p>",
            previousAnalysis: [
              {
                time: "2023-11-15 10:25:36",
                riskLevel: "medium",
                summary: "检测到右肺上叶疑似结节，建议定期随访观察",
                keyFindings: "<ul><li><b>右肺上叶结节</b>：8mm×7mm的小结节，边界相对清晰，暂无明显毛刺征。</li><li><b>风险评估</b>：根据结节大小和形态特征，属于中度疑似病变，建议3个月后复查。</li></ul>"
              }
            ]
          },
          {
            reportId: "AI2024050002",
            patientName: "苏雅婷",
            patientInfo: "苏雅婷，58岁，男，糖尿病",
            patientAge: 58,
            patientGender: "男",
            medicalRecordNo: "P202405002",
            contactPhone: "139****5678",
            reportType: "血液检验",
            disease: "肝功能异常",
            analyzeTime: "2024-05-12 09:15:43",
            riskLevel: "medium",
            summary: "检测到轻度贫血及肝功能异常，建议进一步检查",
            keyFindings: "<ul><li><b>血红蛋白(HGB) 108 g/L</b>：低于正常范围，提示轻度贫血，可能与营养不良、慢性失血或某些疾病相关。</li><li><b>丙氨酸氨基转移酶(ALT) 63 U/L</b>：轻度升高，提示肝脏功能受损。</li><li><b>天门冬氨酸氨基转移酶(AST) 58 U/L</b>：轻度升高，常见于肝细胞损伤。</li></ul>",
            suggestions: "<ul><li>完善肝炎病毒标志物检查</li><li>复查血常规及肝功能（2-4周后）</li><li>肝脏超声检查排除结构性病变</li><li>咨询消化内科医师进一步诊治</li></ul>",
            education: "<p>肝功能检查是反映肝脏健康状况的重要指标。转氨酶升高提示肝细胞损伤，可能由多种原因引起，包括病毒性肝炎、药物性肝损伤、脂肪肝等。及早发现并治疗肝功能异常，可避免肝脏进一步损伤。</p>",
            originalReport: "<p><b>检查名称：</b> 血常规+肝功能</p><p><b>检查时间：</b> 2024-05-12</p><p><b>检查结果：</b></p><table class=\"report-table\"><tr><th>检测项目</th><th>结果</th><th>参考范围</th></tr><tr><td>白细胞(WBC)</td><td>6.5×10^9/L</td><td>4.0-10.0×10^9/L</td></tr><tr><td>红细胞(RBC)</td><td>4.2×10^12/L</td><td>3.5-5.5×10^12/L</td></tr><tr class=\"abnormal\"><td>血红蛋白(HGB)</td><td>108 g/L</td><td>120-160 g/L</td></tr><tr class=\"abnormal\"><td>丙氨酸氨基转移酶(ALT)</td><td>63 U/L</td><td>≤ 40 U/L</td></tr><tr class=\"abnormal\"><td>天门冬氨酸氨基转移酶(AST)</td><td>58 U/L</td><td>≤ 40 U/L</td></tr><tr><td>总胆红素(TBIL)</td><td>15.2 μmol/L</td><td>≤ 21 μmol/L</td></tr></table>",
            previousAnalysis: []
          },
          {
            reportId: "AI2024050003",
            patientName: "林书辰",
            patientInfo: "林书辰，72岁，男，前列腺增生",
            patientAge: 72,
            patientGender: "男",
            medicalRecordNo: "P202405003",
            contactPhone: "135****4321",
            reportType: "超声检查",
            disease: "前列腺癌",
            analyzeTime: "2024-05-08 16:45:18",
            riskLevel: "high",
            summary: "发现前列腺实质不均质回声区，高度怀疑恶性肿瘤",
            keyFindings: "<ul><li><b>前列腺大小</b>：前列腺增大，大小约48mm×45mm×50mm，体积约57ml。</li><li><b>前列腺右叶后外侧</b>：见一低回声区，大小约15mm×12mm，形态不规则，边界不清，内部回声不均。</li><li><b>前列腺动脉血流</b>：低回声区内可见丰富的不规则血流信号。</li></ul>",
            suggestions: "<ul><li>建议尽快进行前列腺特异性抗原(PSA)检查</li><li>考虑行经直肠超声引导下前列腺穿刺活检</li><li>建议泌尿外科专科会诊</li></ul>",
            education: "<p>前列腺癌是男性常见的恶性肿瘤之一，发病率随年龄增长显著上升。早期前列腺癌无明显症状，主要通过PSA、直肠指检和超声检查进行筛查。及早发现并治疗前列腺癌可显著提高生存率。</p>",
            originalReport: "<p><b>检查名称：</b> 前列腺彩超</p><p><b>检查时间：</b> 2024-05-08</p><p><b>检查所见：</b></p><p>前列腺增大，大小约48mm×45mm×50mm，体积约57ml。形态饱满，包膜完整。前列腺右叶后外侧见一低回声区，大小约15mm×12mm，形态不规则，边界不清，内部回声不均。CDFI：前列腺内实质血流丰富，低回声区内见丰富的不规则血流信号。膀胱充盈适中，壁光滑，内未见明显异常回声。</p><p><b>超声提示：</b></p><p>1. 前列腺增大，右叶可疑占位，性质待定，建议进一步检查和随访。</p>",
            previousAnalysis: []
          },
          {
            reportId: "AI2024050004",
            patientName: "程若曦",
            patientInfo: "程若曦，60岁，女，高血压",
            patientAge: 60,
            patientGender: "女",
            medicalRecordNo: "P202405004",
            contactPhone: "137****9876",
            reportType: "心电图",
            disease: "心律不齐",
            analyzeTime: "2024-05-15 11:30:54",
            riskLevel: "low",
            summary: "发现窦性心律，偶发室性早搏，无明显缺血性改变",
            keyFindings: "<ul><li><b>心率</b>：75次/分，总体规律，偶见室性早搏</li><li><b>P波</b>：形态正常</li><li><b>PR间期</b>：正常范围内，0.16s</li><li><b>QRS波群</b>：形态正常，宽度0.08s</li><li><b>ST段</b>：未见明显抬高或压低</li><li><b>T波</b>：未见明显异常</li></ul>",
            suggestions: "<ul><li>建议定期复查心电图，如症状加重，及时就医</li><li>规律服用降压药物，监测血压</li><li>减少咖啡因摄入，保持充分休息</li></ul>",
            education: "<p>室性早搏是一种常见的心律失常，多数情况下属于良性，尤其是在无器质性心脏病患者中。规律生活作息、避免过度疲劳、戒烟限酒、适当控制咖啡摄入等措施有助于减少早搏发作。</p>",
            originalReport: "<p><b>检查名称：</b> 十二导联心电图</p><p><b>检查时间：</b> 2024-05-15</p><p><b>检查所见：</b></p><p>心率75次/分，窦性心律，偶发室性早搏。P波、PR间期正常。QRS波群宽0.08s，形态正常。ST段未见明显抬高或压低，T波形态未见明显异常。QT间期正常。心电轴正常。</p><p><b>心电图结论：</b></p><p>1. 窦性心律</p><p>2. 偶发室性早搏</p>",
            previousAnalysis: []
          },
          {
            reportId: "AI2024050005",
            patientName: "郭子轩",
            patientInfo: "郭子轩，45岁，男，胃溃疡",
            patientAge: 45,
            patientGender: "男",
            medicalRecordNo: "P202405005",
            contactPhone: "136****5432",
            reportType: "胃镜检查",
            disease: "胃溃疡",
            analyzeTime: "2024-05-05 14:20:40",
            riskLevel: "medium",
            summary: "胃窦部发现一处活动性溃疡，无明显恶性征象",
            keyFindings: "<ul><li><b>胃窦部</b>：发现一处直径约0.8cm的溃疡，边缘整齐，基底洁净，周围黏膜充血水肿</li><li><b>幽门螺杆菌</b>：快速尿素酶试验阳性，提示幽门螺杆菌感染</li><li><b>其他部位</b>：食管、贲门、胃底、胃体及十二指肠球部粘膜未见明显异常</li></ul>",
            suggestions: "<ul><li>建议标准四联疗法根除幽门螺杆菌感染</li><li>口服质子泵抑制剂治疗溃疡</li><li>建议规律饮食，避免刺激性食物</li><li>治疗后3个月复查胃镜评估溃疡愈合情况</li></ul>",
            education: "<p>胃溃疡是消化性溃疡的一种常见形式，多由幽门螺杆菌感染、长期服用非甾体抗炎药物、精神压力大等因素导致。标准除菌治疗和质子泵抑制剂是主要治疗方式。规律饮食、戒烟限酒、减轻心理压力有助于预防复发。</p>",
            originalReport: "<p><b>检查名称：</b> 电子胃镜</p><p><b>检查时间：</b> 2024-05-05</p><p><b>检查所见：</b></p><p>食管：粘膜光滑，未见异常。贲门：齿状线清晰，关闭良好。胃：胃壁蠕动正常，胃底、胃体粘膜未见明显异常；胃窦部见一处直径约0.8cm的溃疡，边缘整齐，基底洁净，周围黏膜充血水肿。幽门：形态正常，开放良好。十二指肠：球部粘膜未见明显异常。快速尿素酶试验：阳性。</p><p><b>胃镜诊断：</b></p><p>1. 胃窦溃疡（A2期）</p><p>2. 幽门螺杆菌感染</p>",
            previousAnalysis: [
              {
                time: "2023-12-10 15:35:22",
                riskLevel: "high",
                summary: "胃窦部发现一处较大活动性溃疡，伴有活动性出血",
                keyFindings: "<ul><li><b>胃窦部</b>：发现一处直径约1.2cm的溃疡，边缘欠整齐，基底见少量活动性出血，周围黏膜明显充血水肿</li><li><b>幽门螺杆菌</b>：快速尿素酶试验强阳性</li></ul>"
              }
            ]
          }
        ]
      };
    },
    mounted() {
      this.getHistoryList();
    },
    watch: {
      dateRange(val) {
        if (val) {
          this.queryParams.beginTime = val[0];
          this.queryParams.endTime = val[1];
        } else {
          this.queryParams.beginTime = "";
          this.queryParams.endTime = "";
        }
      }
    },
    methods: {
      // 获取历史记录列表（模拟数据）
      getHistoryList() {
        this.loading = true;
        
        // 模拟后端查询和筛选逻辑
        setTimeout(() => {
          let filteredData = [...this.mockData];
          
          // 患者姓名过滤
          if (this.queryParams.patientName) {
            const keyword = this.queryParams.patientName.toLowerCase();
            filteredData = filteredData.filter(item => 
              item.patientName.toLowerCase().includes(keyword)
            );
          }
          
          // 报告类型过滤
          if (this.queryParams.reportType) {
            filteredData = filteredData.filter(item => 
              item.reportType === this.queryParams.reportType
            );
          }
          
          // 风险等级过滤
          if (this.queryParams.riskLevel) {
            filteredData = filteredData.filter(item => 
              item.riskLevel === this.queryParams.riskLevel
            );
          }
          
          // 时间范围过滤
          if (this.queryParams.beginTime && this.queryParams.endTime) {
            filteredData = filteredData.filter(item => {
              const reportDate = item.analyzeTime.split(' ')[0];
              return reportDate >= this.queryParams.beginTime && reportDate <= this.queryParams.endTime;
            });
          }
          
          this.total = filteredData.length;
          
          // 分页处理
          const start = (this.queryParams.pageNum - 1) * this.queryParams.pageSize;
          const end = start + this.queryParams.pageSize;
          this.historyList = filteredData.slice(start, end);
          
          this.loading = false;
        }, 500);
      },
      
      // 查询按钮
      handleQuery() {
        this.queryParams.pageNum = 1;
        this.getHistoryList();
      },
      
      // 重置查询条件
      resetQuery() {
        this.dateRange = [];
        this.queryParams = {
          pageNum: 1,
          pageSize: 10,
          reportType: "",
          patientName: "",
          riskLevel: "",
          beginTime: "",
          endTime: ""
        };
        this.handleQuery();
      },
      
      // 分页大小改变
      handleSizeChange(val) {
        this.queryParams.pageSize = val;
        this.getHistoryList();
      },
      
      // 页码改变
      handleCurrentChange(val) {
        this.queryParams.pageNum = val;
        this.getHistoryList();
      },
      
      // 多选框选中数据
      handleSelectionChange(selection) {
        this.selection = selection;
      },
      
      // 获取风险等级标签类型
      getRiskTag(risk) {
        switch(risk) {
          case 'high': return 'danger';
          case 'medium': return 'warning';
          case 'low': return 'success';
          default: return 'info';
        }
      },
      
      // 获取报告类型标签
      getReportTypeTag(type) {
        switch(type) {
          case 'CT影像': return '';
          case '核磁共振': return 'info';
          case '血液检验': return 'success';
          case '超声检查': return 'warning';
          case '心电图': return 'danger';
          default: return '';
        }
      },
      
      // 获取风险等级文本
      getRiskText(risk) {
        const riskMap = {
          high: "高度关注",
          medium: "中度关注",
          low: "一般关注"
        };
        return riskMap[risk] || "一般关注";
      },
      
      // 获取时间线颜色
      getTimelineColor(risk) {
        switch(risk) {
          case 'high': return '#F56C6C';
          case 'medium': return '#E6A23C';
          case 'low': return '#67C23A';
          default: return '#909399';
        }
      },
      
      // 查看详情
      viewDetail(row) {
        this.currentReport = row;
        this.dialogVisible = true;
      },
      
      // 导出报告
      exportReport(row) {
        this.$message.success(`报告 ${row.reportId} 已导出为PDF文件`);
      },
      
      // 删除报告
      deleteReport(row) {
        this.$confirm(`确定删除 ${row.patientName} 的 ${row.reportType} 解读记录吗?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          // 模拟删除操作
          this.historyList = this.historyList.filter(item => item.reportId !== row.reportId);
          this.total -= 1;
          this.$message.success('删除成功');
        }).catch(() => {
          this.$message.info('已取消删除');
        });
      },
      
      // 批量导出
      batchExport() {
        if (this.selection.length === 0) {
          this.$message.warning('请至少选择一条记录');
          return;
        }
        this.$message.success(`已成功导出 ${this.selection.length} 份报告`);
      },
      
      // 批量删除
      batchDelete() {
        if (this.selection.length === 0) {
          this.$message.warning('请至少选择一条记录');
          return;
        }
        
        this.$confirm(`确定删除已选择的 ${this.selection.length} 条解读记录吗?`, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          // 模拟批量删除操作
          const ids = this.selection.map(item => item.reportId);
          this.historyList = this.historyList.filter(item => !ids.includes(item.reportId));
          this.total -= this.selection.length;
          this.$message.success(`已成功删除 ${this.selection.length} 条记录`);
          this.selection = [];
        }).catch(() => {
          this.$message.info('已取消删除');
        });
      },
      
      // 关闭详情弹窗
      handleClose() {
        this.dialogVisible = false;
        this.currentReport = null;
      },
      
      // 打印报告
      printReport() {
        this.$message.success('正在打印报告...');
        // 实际应用中应使用window.print()或专门的打印库
      },
      
      // 分享给医生
      shareToDoctors() {
        this.$prompt('请输入要分享的医生姓名', '分享报告', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPattern: /\S/,
          inputErrorMessage: '医生姓名不能为空'
        }).then(({ value }) => {
          this.$message({
            type: 'success',
            message: `报告已成功分享给医生：${value}`
          });
        }).catch(() => {
          this.$message({
            type: 'info',
            message: '已取消分享'
          });        
        });
      },
      
      // 对比历史记录
      compareTo(historyItem) {
        this.compareData = {
          previous: historyItem,
          hasImproved: this.getRiskLevel(historyItem.riskLevel) > this.getRiskLevel(this.currentReport.riskLevel),
          hasWorsened: this.getRiskLevel(historyItem.riskLevel) < this.getRiskLevel(this.currentReport.riskLevel),
          changeAnalysis: `<ul>
            <li><b>指标变化：</b>与上次相比，当前检查结果显示病灶大小由8mm×7mm增长至15mm×12mm，形态由相对规则变为不规则，边缘模糊，出现毛刺征和胸膜凹陷征。</li>
            <li><b>风险变化：</b>结节恶性可能性明显增加，建议立即进行进一步检查。</li>
          </ul>`
        };
        this.compareDialogVisible = true;
      },
      
      // 获取风险等级数值（用于比较）
      getRiskLevel(risk) {
        switch(risk) {
          case 'high': return 3;
          case 'medium': return 2;
          case 'low': return 1;
          default: return 0;
        }
      }
    }
  }
  </script>
  
  <style lang="scss" scoped>
  .filter-card {
    margin-bottom: 20px;
    
    .search-form {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 0;
    }
  }
  
  .stat-row {
    margin-bottom: 20px;
    
    .stat-card {
      height: 100%;
      box-shadow: 0 2px 12px 0 rgba(0,0,0,.05);
      
      .stat-content {
        display: flex;
        align-items: center;
        
        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
          
          i {
            font-size: 24px;
            color: white;
          }
        }
        
        .stat-info {
          flex: 1;
          
          .stat-title {
            font-size: 14px;
            color: #606266;
            margin-bottom: 8px;
          }
          
          .stat-value {
            font-size: 22px;
            font-weight: 600;
            color: #303133;
          }
        }
      }
    }
  }
  
  .table-card {
    .clearfix {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    
    .operation-buttons {
      display: flex;
      gap: 10px;
    }
    
    .pagination-container {
      margin-top: 20px;
      text-align: right;
    }
  }
  
  .report-detail {
    .patient-info {
      margin-bottom: 20px;
      
      h3 {
        margin-bottom: 15px;
        font-weight: 600;
        font-size: 16px;
        color: #303133;
      }
    }
    
    .analysis-result {
      .risk-panel {
        margin-bottom: 20px;
      }
      
      .action-buttons {
        margin-top: 30px;
        text-align: center;
      }
    }
    
    .risk-level {
      padding: 8px 12px;
      border-radius: 4px;
      display: inline-block;
      margin-bottom: 15px;
      font-weight: bold;
      
      &.high {
        background-color: rgba(245, 108, 108, 0.1);
        color: #F56C6C;
      }
      
      &.medium {
        background-color: rgba(230, 162, 60, 0.1);
        color: #E6A23C;
      }
      
      &.low {
        background-color: rgba(103, 194, 58, 0.1);
        color: #67C23A;
      }
    }
    
    .analysis-summary {
      font-size: 16px;
      margin-bottom: 15px;
      font-weight: 500;
    }
    
    .origin-report {
      background-color: #f8f9fa;
      padding: 15px;
      border-radius: 4px;
      border: 1px solid #e9ecef;
    }
  }
  
  .compare-container {
    .compare-panel {
      border: 1px solid #EBEEF5;
      border-radius: 4px;
      padding: 15px;
      height: 100%;
      min-height: 300px;
      
      .compare-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 10px;
        border-bottom: 1px solid #EBEEF5;
        margin-bottom: 15px;
        
        h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
        }
      }
    }
    
    .change-analysis {
      margin-top: 20px;
      
      .improved {
        color: #67C23A;
        font-weight: 500;
      }
      
      .worsened {
        color: #F56C6C;
        font-weight: 500;
      }
      
      .stable {
        color: #E6A23C;
        font-weight: 500;
      }
    }
  }
  </style>